import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Divider, useTheme } from '@mui/material';
import { FormikProvider, useFormik } from 'formik';
import { debounce, isEqual } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { routes, URL_VARS } from '../../../../constants/routing';
import {
  getInitialTemplateBuilderValues,
  TemplateModalSteps,
  TemplateModalValidationSchema,
} from '../../../../constants/templates';
import { useTemplateMediaUpload } from '../../../../hooks/useTemplateImageUpload';
import { setClaimLinkBuilderDraft, updateClaimLinkBuilderField } from '../../../../store/actions/templates';
import { selectCurrentOrganizationLogoUrl } from '../../../../store/selectors/organizations';
import { selectDefaultExperienceBlueprint } from '../../../../store/selectors/shell';
import { selectClaimLinkBuilderDraft } from '../../../../store/selectors/templates';
import { IClaimLinkBuilderValues, ITemplateModalStep, TemplateModalStepEnum } from '../../../../types/templates';
import { BrandingStep, DetailsStep, HeroStep, ModalFooter, ModalHeader, NavigationMenu } from './components';

interface IProps {
  open: boolean;
  onClose: () => void;
  isEditMode?: boolean;
}

const TemplateManagerModal = ({ open, onClose, isEditMode = false }: IProps) => {
  const theme = useTheme();
  const history = useHistory();
  const dispatch = useDispatch();
  const templateDraft = useSelector(selectClaimLinkBuilderDraft);
  const blueprint = useSelector(selectDefaultExperienceBlueprint);
  const organizationLogoUrl = useSelector(selectCurrentOrganizationLogoUrl);

  const [activeStep, setActiveStep] = useState<number>(
    isEditMode ? TemplateModalStepEnum.Branding : TemplateModalSteps[0].id,
  );

  const formikInitialValues = useMemo(() => {
    if (isEditMode) {
      return templateDraft;
    }
    return getInitialTemplateBuilderValues(blueprint, organizationLogoUrl);
  }, [isEditMode, templateDraft, blueprint, organizationLogoUrl]);

  const formik = useFormik<IClaimLinkBuilderValues>({
    initialValues: formikInitialValues,
    enableReinitialize: true,
    validationSchema: TemplateModalValidationSchema,
    onSubmit: () => {
      onClose();
    },
  });

  const { templateName } = formik.values;
  const { setFieldValue, resetForm } = formik;

  const handleMediaSuccess = useCallback(
    (fieldName: keyof IClaimLinkBuilderValues, value: string | null) => {
      setFieldValue(fieldName, value || '');
    },
    [setFieldValue],
  );

  const { handleMediaUpload, handleMediaRemove } = useTemplateMediaUpload({ onSuccess: handleMediaSuccess });

  const handleFieldChange = useCallback(
    <K extends keyof IClaimLinkBuilderValues>(fieldName: K) =>
      (value: IClaimLinkBuilderValues[K]) => {
        setFieldValue(fieldName, value);
      },
    [setFieldValue],
  );

  const handleSaveChanges = useCallback(() => {
    if (isEditMode) {
      dispatch(updateClaimLinkBuilderField(formik.values));
    } else {
      dispatch(setClaimLinkBuilderDraft(formik.values));
    }
  }, [dispatch, formik.values, isEditMode]);

  // In edit mode, mirror dialog changes into the builder draft as they happen so the canvas
  // previews them live. Debounced so color-picker drags don't flood the draft/undo history.
  const applyDraftUpdateDebounced = useMemo(
    () => debounce((values: IClaimLinkBuilderValues) => dispatch(updateClaimLinkBuilderField(values)), 300),
    [dispatch],
  );

  useEffect(() => {
    if (isEditMode && open && !isEqual(formik.values, templateDraft)) {
      applyDraftUpdateDebounced(formik.values);
    }
  }, [formik.values, isEditMode, open, templateDraft, applyDraftUpdateDebounced]);

  useEffect(() => {
    return () => {
      applyDraftUpdateDebounced.cancel();
    };
  }, [applyDraftUpdateDebounced]);

  const handleCloseModal = useCallback(() => {
    if (isEditMode) {
      applyDraftUpdateDebounced.cancel();
      handleSaveChanges();
    }
    setActiveStep(isEditMode ? TemplateModalStepEnum.Branding : TemplateModalStepEnum.Details);
    resetForm();
    onClose();
  }, [onClose, isEditMode, handleSaveChanges, resetForm, applyDraftUpdateDebounced]);

  const isFirstStep = useMemo(() => activeStep === TemplateModalStepEnum.Details, [activeStep]);

  const isLastStep = useMemo(() => activeStep === TemplateModalStepEnum.HeroDefaults, [activeStep]);

  const isContinueDisabled = useMemo(() => isFirstStep && !templateName?.trim(), [isFirstStep, templateName]);

  const handleStepClick = useCallback(
    (step: ITemplateModalStep) => {
      if (isContinueDisabled && !isEditMode) {
        return;
      }
      setActiveStep(step.id);
    },
    [isContinueDisabled, isEditMode],
  );

  const handleContinue = useCallback(() => {
    if (isLastStep) {
      handleSaveChanges();

      history.push(
        routes.templates.getTemplateUrl({
          type: URL_VARS.CLAIM_LINK,
          flowId: URL_VARS.NEW,
        }),
      );

      handleCloseModal();
      return;
    }
    setActiveStep(activeStep + 1);
  }, [activeStep, isLastStep, history, handleCloseModal, handleSaveChanges]);

  const stepContent = useMemo(() => {
    switch (activeStep) {
      case TemplateModalStepEnum.Details:
        return (
          <DetailsStep
            formik={formik}
            handleDepartmentChange={handleFieldChange}
            handleFileUpload={handleMediaUpload}
            handleFileRemove={handleMediaRemove}
          />
        );
      case TemplateModalStepEnum.Branding:
        return <BrandingStep />;
      case TemplateModalStepEnum.HeroDefaults:
        return <HeroStep />;
      default:
        return null;
    }
  }, [activeStep, formik, handleFieldChange, handleMediaUpload, handleMediaRemove]);

  return (
    <FormikProvider value={formik}>
      <Dialog
        open={open}
        onClose={handleCloseModal}
        hideBackdrop={isEditMode}
        sx={{ '& .MuiDialog-paper': { width: 900, maxWidth: 900, height: 650 } }}
      >
        <DialogTitle
          component="div"
          sx={{ position: 'relative', borderBottom: `1px solid ${theme.palette.divider}`, fontSize: 0, lineHeight: 0 }}
        >
          <ModalHeader handleCloseModal={handleCloseModal} isEditMode={isEditMode} />
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'row', p: 0, overflow: 'hidden' }}>
          <NavigationMenu
            isEditMode={isEditMode}
            activeStep={activeStep}
            steps={TemplateModalSteps}
            handleStepClick={handleStepClick}
          />
          <Divider orientation="vertical" flexItem />
          <Box sx={{ flex: 1, px: 2, pt: 1.75, pb: 2, overflowY: 'auto' }}>
            <Box sx={{ px: 2, gap: 1, display: 'flex', flexDirection: 'column' }}>{stepContent}</Box>
          </Box>
        </DialogContent>
        {!isEditMode && (
          <DialogActions
            sx={{
              justifyContent: 'space-between',
              px: 2,
              py: 1,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <ModalFooter
              isFirstStep={isFirstStep}
              isLastStep={isLastStep}
              isContinueDisabled={isContinueDisabled}
              handleContinue={handleContinue}
            />
          </DialogActions>
        )}
      </Dialog>
    </FormikProvider>
  );
};

export default TemplateManagerModal;
